import { useRef } from 'react'

/** The file a presigned URL addresses, ignoring the signature granting access. */
function fileIdentity(url: string | undefined) {
  if (!url) return url

  try {
    const { origin, pathname } = new URL(url)
    return origin + pathname
  } catch {
    return url
  }
}

interface UseCanvasFilePathProps {
  /** The page's presigned download URL, straight from the query. */
  downloadUrl: string | undefined
}

/**
 * The file to hand the renderer, which reloads whenever this string changes.
 *
 * Presentation-file URLs are presigned, so the same file comes back under a
 * fresh `Expires` and `Signature` on every refetch - and moving between views
 * refetches. Left alone that reads to the renderer as a different file: it
 * disposes, refetches and redraws the document already on screen, which is
 * visible as the entity appearing and then loading a second time.
 *
 * This is the guard that used to live on the frame view's fragment path, where
 * the file path was memoised so a changed `imageToken` could not recompute it
 * (Cloud#17935). That path is gone - every view loads the page's file now - so
 * the guard belongs on the page URL: the file is identified by where it lives
 * rather than by the whole signed string, so only a genuinely different file
 * reloads.
 */
export function useCanvasFilePath({ downloadUrl }: UseCanvasFilePathProps) {
  const pinned = useRef(downloadUrl)

  if (!pinned.current || !downloadUrl) {
    pinned.current = downloadUrl
    return pinned.current
  }

  if (downloadUrl === pinned.current) return pinned.current

  // The same file, signed again.
  if (fileIdentity(downloadUrl) === fileIdentity(pinned.current)) {
    return pinned.current
  }

  pinned.current = downloadUrl
  return pinned.current
}
